/**
 * The feed's two small invitations to Ask: the channel note at the top, and the
 * "Explain simply" chip at the foot.
 *
 * Lifted out of `Feed.tsx` so the feed's own file changes by a line each.
 */

import { useState, useSyncExternalStore } from 'react'
import type { ReasonNode } from '../core/types'
import { EXPLAIN_SIMPLY, clearConversation, useComposerEmpty } from './askLive'
import { onVoiceChange, setVoiceOn, voiceOn } from './narrator'

/**
 * The channel's contract, in ONE line, with the rest one press away.
 *
 * It was a paragraph of four sentences at the head of every conversation, and
 * the study read it as the thing that replaced the openers after the first
 * narration: the invitation went, the caveat stayed. The line keeps the two
 * facts that change how an answer is read — the channel has no search, and what
 * is asked stays in this browser — and "More" holds every other sentence it
 * carried, unchanged in substance: V for a check, an answer pressed into a pass
 * travels in that pass's receipt (D42), the short notes after each pass travel
 * with the exploration in its exports and share links (D91, D133, D175), and the
 * voice's own disclosure with its switch. The reasons are in docs/guide.md
 * under "Asking about one node".
 *
 * The cost is the voice's. Its sentence — each note is a small extra model call,
 * and every press's estimate includes it (D295) —
 * sat at the head of the conversation, beside its off switch, because a call the
 * reader did not press one by one should be named where it speaks; now it is one
 * press away, and what stays at rest is each note's own "Narration · not
 * checked" mark. It fits one line at the thread's width; on a phone it
 * wraps to two.
 */
export function ChannelNote({
  conversationKey,
  hasTurns,
}: {
  /** The live conversation's key, for Clear. */
  conversationKey: string
  hasTurns: boolean
}) {
  const voice = useSyncExternalStore(onVoiceChange, voiceOn, voiceOn)
  const [more, setMore] = useState(false)
  return (
    <p className="gnote gnote--quiet feed-note">
      Answers read the graph, not sources. Questions stay in this browser.{' '}
      {more ? (
        <>
          Press <span className="kbd">V</span> on a claim to check it against sources. An answer you
          send into a pass travels in that pass’s receipt, and the short notes after each pass
          travel with the exploration, in its exports and share links.{' '}
          {voice ? (
            <>
              Each note is a small extra model call, and every press’s estimate includes it.{' '}
              <button className="linkish" onClick={() => setVoiceOn(false)}>
                Quiet the voice
              </button>
            </>
          ) : (
            <>
              The voice is off — passes land silently.{' '}
              <button className="linkish" onClick={() => setVoiceOn(true)}>
                Turn it back on
              </button>
            </>
          )}{' '}
          {hasTurns && (
            <button className="linkish" onClick={() => clearConversation(conversationKey)}>
              Clear the conversation
            </button>
          )}{' '}
          <button className="linkish" aria-expanded onClick={() => setMore(false)}>
            Less
          </button>
        </>
      ) : (
        <button className="linkish" aria-expanded={false} onClick={() => setMore(true)}>
          More
        </button>
      )}
    </p>
  )
}

/**
 * "Explain simply", whenever a node is selected and the box is empty.
 *
 * The four openers that preceded it lived above the record and went away with
 * the first turn — which after a pass is the voice's narration, so in practice
 * they were gone before anybody asked anything. This one sits at the feed's
 * foot, directly above the box it stands in for, and stays: every node can be
 * asked to be put plainly, and the answer is the channel's clearest text. The
 * openers were kept off the foot because a BLOCK of four suggestions there held
 * a streaming pass off the screen's edge; one chip, one line high, does not.
 *
 * It is an Ask, sent the way the openers were sent (`send`, the feed's own):
 * a chat call on this press, about the selected node, no search, no lock and no
 * step of history. Nothing here queues or spends by itself (invariant 9). It
 * stands down while a question is being typed — on a phone the sheet's own box,
 * on a desktop the composer (`useComposerEmpty`) — and while an answer is
 * arriving, which `send` would refuse anyway.
 */
export function ExplainSimply({
  selected,
  ready,
  narrow,
  phoneDraft,
  answering,
  elsewhere,
  onAsk,
}: {
  /** The node the question would be about — the feed's own subject rule. */
  selected: ReasonNode | null
  /** Whether a question could be sent at all (`useCanCall`). */
  ready: boolean
  narrow: boolean
  /** The phone sheet's own box, which is the Ask box there. */
  phoneDraft: string
  answering: boolean
  elsewhere: boolean
  onAsk: (question: string) => void
}) {
  const composerEmpty = useComposerEmpty()
  const boxEmpty = narrow ? phoneDraft.trim() === '' : composerEmpty
  if (!selected || !boxEmpty || answering) return null
  return (
    // One chip, as wide as its words: the column the four used to fill is gone.
    <div className="askthread-openers" style={{ alignItems: 'flex-start' }}>
      <button
        className="ask-opener"
        disabled={!ready || elsewhere}
        title={
          !ready
            ? 'Connect a key to ask.'
            : elsewhere
              ? 'Another answer is still arriving.'
              : `Asks, about “${selected.label}”: ${EXPLAIN_SIMPLY.question} One model call; no search.`
        }
        onClick={() => onAsk(EXPLAIN_SIMPLY.question)}
      >
        {EXPLAIN_SIMPLY.label}
      </button>
    </div>
  )
}
